import { useQuery } from '@apollo/client';
import moment, { Moment as TMoment } from 'moment';
import React, { useState, useEffect } from 'react';

import { FallbackWrapper, BookingCalendarTimeContainer } from './BookingCalendarTime.styled';
import { AdditionalInfo } from './components/AdditionalInfo/AdditionalInfo';
import BookingCalendar from './components/BookingCalendar';
import BookingTimeSelection from './components/BookingTimeSelection';
import { Loader } from 'components/Loader/Loader';
import { usePatient } from 'hooks/usePatient/usePatient';
import { TimeSlot } from 'kb-shared';
import { useAppointmentTypeFilter } from 'kb-shared/booking/hooks/useAppointmentTypeFilter';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import {
  mappedTimeZone,
  MOMENT_TIME_ZONES,
  momentInTimeZone
} from 'kb-shared/utilities/momentHelpers';
import { AppointmentTypeFilter } from 'screens/Book/Book.types';
import { getMonthStartDateTime } from 'utilities/formatDate';

import { AdditionalInfoPanel } from '../AdditionalInfoPanel';
import { DATE_TIME_SLOTS_QUERY } from './BookingCalendarTime.graphql';
import { Props, TimeSlotsResults } from './BookingCalendarTime.types';
import {
  requiresPointerToDoctorPages,
  cleanResults,
  findSelectedTimeSlots
} from './BookingCalendarTime.utils';
import { DEFAULT_FALLBACK } from './MissingTimeSlotsFallback';

const SEARCH_MONTHS_IN_ADVANCE = 6;

export const BookingCalendarTime = (props: Props): JSX.Element => {
  const [selectedDate, setSelectedDate] = useState<TMoment | undefined>(
    props.date ? moment(props.date) : undefined
  );
  const [timeSlotsMonthLoadAttempt, setTimeSlotsMonthLoadAttempt] = useState(0);
  const { patient } = usePatient();
  const preferredTimezone = patient?.preferredTimezone as keyof typeof MOMENT_TIME_ZONES;
  const labTimezone = patient?.lab?.timeZone;

  const [currentMonth, setCurrentMonth] = useState(
    moment.tz(mappedTimeZone(preferredTimezone || labTimezone || 'UTC'))
  );
  const [appointmentFound, setAppointmentFound] = useState<boolean>(false);
  const today = moment();

  const adjustSelectedDate = (selectedDate: TMoment) => {
    setSelectedDate(selectedDate);
    props.onSelectTimeSlot(null);
  };

  const adjustCurrentMonth = (adjustMonth: number) => {
    setSelectedDate(undefined);
    setCurrentMonth(currentMonth.clone().add(adjustMonth, 'months'));
    setTimeSlotsMonthLoadAttempt(timeSlotsMonthLoadAttempt + 1);
    props.onSelectTimeSlot(null);
  };

  const selectedAppointmentTypeFilter = useAppointmentTypeFilter();

  const filter = getFilterForQuery(selectedAppointmentTypeFilter);

  const locationId =
    typeof props.locationId === 'string' ? parseInt(props.locationId) : props.locationId;

  const isVirtual = props.appointmentType.virtual;
  const appointmentTypeId = parseInt(props.appointmentType.id);

  const startDateTime = props.date
    ? `${props.date}T00:00:00.000Z`
    : getMonthStartDateTime(currentMonth);
  const endDateTime = props.date ? `${props.date}T23:59:59.999Z` : undefined;

  const { data, loading } = useQuery<TimeSlotsResults>(DATE_TIME_SLOTS_QUERY, {
    fetchPolicy: 'no-cache',
    variables: {
      appointmentTypeId: appointmentTypeId,
      locationId: locationId ? locationId : null,
      startDateTime: startDateTime,
      endDateOffsetInMonths: 1,
      ...(endDateTime && { endDateTime: endDateTime }),
      labId: props.labId,
      providerId: props.providerId,
      appointmentId: props.appointmentId,
      filter: filter
    },
    onError: error => BugTracker.notify(error, 'DateTimeSlotsQuery'),
    skip: !props.appointmentType || (!props.appointmentType.hasTimeSlots && !props.reschedule)
  });

  const linkedAppointment = props.appointmentType.inClinicOrVirtualEquivalent;
  const linkedAppointmentTypeId = parseInt(linkedAppointment?.id ?? '');

  const { data: linkedAppointmentData, loading: loadingLinkedAppointmentData } =
    useQuery<TimeSlotsResults>(DATE_TIME_SLOTS_QUERY, {
      fetchPolicy: 'no-cache',
      variables: {
        appointmentTypeId: linkedAppointmentTypeId,
        locationId: locationId ? locationId : null,
        startDateTime: startDateTime,
        endDateOffsetInMonths: 1,
        ...(endDateTime && { endDateTime: endDateTime }),
        labId: props.labId,
        providerId: props.providerId,
        appointmentId: props.appointmentId,
        filter: filter
      },
      onError: error => BugTracker.notify(error, 'DateTimeSlotsQuery'),
      skip: !linkedAppointment || (!linkedAppointment.hasTimeSlots && !props.reschedule)
    });

  const isLoadingTimeSlots =
    (loading || loadingLinkedAppointmentData) &&
    timeSlotsMonthLoadAttempt >= 0 &&
    timeSlotsMonthLoadAttempt < SEARCH_MONTHS_IN_ADVANCE;

  useEffect(() => {
    if (
      ((!data || !data.dateTimeSlots) &&
        (!linkedAppointmentData || !linkedAppointmentData.dateTimeSlots)) ||
      loading ||
      loadingLinkedAppointmentData
    )
      return;

    const dateTimeSlots: Array<TimeSlot> = getDateTimeSlots(data, linkedAppointmentData);

    if (!selectedDate && dateTimeSlots.length > 0) {
      const sortedTimeSlots = dateTimeSlots.sort(
        (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      );
      const defaultDate = momentInTimeZone(
        sortedTimeSlots[0].startTime,
        sortedTimeSlots[0].timeZone
      ).startOf('day');
      setSelectedDate(defaultDate);
    }

    if (
      !appointmentFound &&
      !dateTimeSlots?.length &&
      timeSlotsMonthLoadAttempt < SEARCH_MONTHS_IN_ADVANCE
    ) {
      // try to load time slots for the next month, up to a year ahead
      adjustCurrentMonth(1);
      return;
    }

    setAppointmentFound(true);
    props.onAppointmentsFound?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    linkedAppointmentData,
    currentMonth,
    loading,
    adjustCurrentMonth,
    appointmentFound,
    today
  ]);

  // Call handleTimeSlotsLoaded when time slots are loaded and available
  useEffect(() => {
    if (
      !isLoadingTimeSlots &&
      appointmentFound &&
      (data?.dateTimeSlots || linkedAppointmentData?.dateTimeSlots)
    ) {
      props.handleTimeSlotsLoaded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingTimeSlots, appointmentFound, data, linkedAppointmentData]);

  const renderCalendarContent = () => {
    if (isLoadingTimeSlots) return <Loader container />;

    if (!appointmentFound) return <FallbackWrapper>{DEFAULT_FALLBACK}</FallbackWrapper>;

    if (
      (!data || !data.dateTimeSlots) &&
      (!linkedAppointmentData || !linkedAppointmentData.dateTimeSlots)
    ) {
      return null;
    }

    const dateTimeSlots: Array<TimeSlot> = getDateTimeSlots(data, linkedAppointmentData);

    const availableTimeSlots = findSelectedTimeSlots(dateTimeSlots, selectedDate);

    if (linkedAppointmentData) {
      availableTimeSlots.sort(
        (appointment1: TimeSlot, appointment2: TimeSlot) =>
          new Date(appointment1.startTime).getTime() - new Date(appointment2.startTime).getTime()
      );
    }

    props.handleTimeSlotsLoaded();

    return (
      <>
        <BookingCalendar
          // @ts-ignore
          currentMonth={currentMonth}
          // @ts-ignore
          selectedDate={selectedDate}
          timeSlotsResults={dateTimeSlots}
          onPrevious={() => {
            adjustCurrentMonth(-1);
          }}
          onNext={() => {
            adjustCurrentMonth(1);
          }}
          onSelectDate={(newDate: TMoment) => {
            adjustSelectedDate(newDate);
          }}
          renderBorder={true}
          currentDayCanBeSelected={props.currentDayCanBeSelected}
        />
        <BookingTimeSelection
          selectedTimeSlot={props.selectedTimeSlot}
          onSelectTimeSlot={props.onSelectTimeSlot}
          timeslots={availableTimeSlots}
          date={selectedDate}
          selectedLabTimeZone={props.selectedLabTimeZone}
          appointmentType={props.appointmentType}
        />
      </>
    );
  };

  const showBottomSection = !props.providerId && appointmentFound;

  return (
    <>
      <BookingCalendarTimeContainer>{renderCalendarContent()}</BookingCalendarTimeContainer>

      {showBottomSection && (
        <AdditionalInfoPanel>
          <AdditionalInfo
            requiresPointerToDoctorPages={requiresPointerToDoctorPages(appointmentTypeId)}
            isVirtual={isVirtual}
          />
        </AdditionalInfoPanel>
      )}
    </>
  );
};

const getDateTimeSlots = (data?: TimeSlotsResults, linkedAppointmentData?: TimeSlotsResults) => {
  const slots = [...(data?.dateTimeSlots || []), ...(linkedAppointmentData?.dateTimeSlots || [])];

  return slots.length > 0 ? cleanResults(slots) : [];
};

const getFilterForQuery = (filter: AppointmentTypeFilter | null) => {
  switch (filter) {
    case 'all':
      return 'ALL';
    case 'virtual':
      return 'VIRTUAL';
    case 'inClinic':
      return 'IN_CLINIC';
    default:
      return 'ALL';
  }
};
